/* Communication Campaigns — thin API client for the Pass C UI.
 *
 * Backed by CampaignController (routes/crm.php). No provider
 * credentials touched here; all sending happens server-side. */

import { API_URL } from "../config";
import { trackedFetch } from "../Components/Loader/GlobalLoader";

const headers = (): HeadersInit => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

export type CampaignChannel        = "email" | "sms";
export type CampaignClassification = "marketing" | "transactional";
export type CampaignPurpose        =
  | "newsletter" | "announcement" | "promotion" | "reminder" | "event" | "general";
export type CampaignStatus =
  | "draft" | "scheduled" | "queued" | "sending" | "sent"
  | "partially_sent" | "failed" | "cancelled" | "archived";

export interface AudienceFilter {
  students?: {
    enabled?: boolean;
    active?: boolean;
    location_ids?: number[];
    grade_ids?: number[];
    subject_ids?: number[];
    program_types?: string[];
    study_modes?: string[];
  };
  parents?: {
    enabled?: boolean;
    of_active_students?: boolean;
    is_email_notification?: boolean;
    is_sms_notification?: boolean;
  };
  staff?: {
    enabled?: boolean;
    active?: boolean;
    location_ids?: number[];
  };
  admins?: {
    enabled?: boolean;
    active?: boolean;
  };
  externals?: Array<{ name?: string; email?: string; phone?: string }>;
}

export interface ICampaign {
  id: number;
  name: string;
  channel: CampaignChannel;
  classification: CampaignClassification;
  purpose: CampaignPurpose;
  template_id: number | null;
  subject: string | null;
  preheader: string | null;
  body: string;
  body_text: string | null;
  audience_filter: AudienceFilter | null;
  status: CampaignStatus;
  scheduled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  total_recipients: number;
  sent_count: number;
  delivered_count: number;
  failed_count: number;
  skipped_count: number;
  opened_count: number;
  created_by: number | null;
  updated_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface ICampaignInput {
  name: string;
  channel: CampaignChannel;
  classification: CampaignClassification;
  purpose: CampaignPurpose;
  template_id?: number | null;
  subject?: string | null;
  preheader?: string | null;
  body: string;
  body_text?: string | null;
  audience_filter?: AudienceFilter | null;
  scheduled_at?: string | null;
}

export interface IPreviewStats {
  total_source: number;
  valid: number;
  duplicates_removed: number;
  suppressed: number;
  invalid_contact: number;
  consent_unknown: number;
  consent_expired: number;
  by_type: Record<string, number>;
}
export interface IPreviewSample {
  name: string | null;
  contact_type: string;
  /** How this recipient was selected — 'students' | 'parents' | 'staff' | 'admins' | 'external'. */
  source_segment?: string;
  address: string;
  /** 'eligible' | 'transactional' | 'suppressed' | 'consent_unknown' | 'no_consent_on_file'
   *  | 'consent_expired' | 'invalid_contact' | 'invalid_address'. */
  eligibility: string;
}
export interface IPreviewResult {
  stats: IPreviewStats;
  samples: IPreviewSample[];
}

interface Envelope<T> { code: number; status: boolean; message?: string; data: T }

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await trackedFetch(`${API_URL}${path}`, { ...init, headers: headers() });
  const json: Envelope<T> = await res.json();
  if (!json.status) throw new Error(json.message || "Request failed");
  return json.data;
}

/* ── Read ───────────────────────────────────────────────────────── */
export const listCampaigns = (params: { status?: string; channel?: string; per_page?: number } = {}) => {
  const qs = new URLSearchParams();
  if (params.status)   qs.set("status", params.status);
  if (params.channel)  qs.set("channel", params.channel);
  if (params.per_page) qs.set("per_page", String(params.per_page));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return call<{ data: ICampaign[]; total: number; current_page: number; last_page: number }>(
    `/campaigns${suffix}`, { method: "GET" },
  );
};

export interface ICampaignOpens {
  trackable_sent: number;
  unique_opens: number;
  total_opens: number;
  open_rate: number | null;
}

export const fetchCampaign = (id: number) =>
  call<{
    campaign: ICampaign;
    source_count: number;
    sent_count: number;
    delivered_count: number;
    failed_count: number;
    skipped_count: number;
    skip_reason_breakdown: Record<string, number>;
    opens: ICampaignOpens;
  }>(`/campaigns/${id}`, { method: "GET" });

/* ── Write ──────────────────────────────────────────────────────── */
export const createCampaign = (data: ICampaignInput) =>
  call<ICampaign>(`/campaigns`, { method: "POST", body: JSON.stringify(data) });

export const updateCampaign = (id: number, data: Partial<ICampaignInput>) =>
  call<ICampaign>(`/campaigns/${id}`, { method: "PUT", body: JSON.stringify(data) });

export const archiveCampaign = (id: number) =>
  call<ICampaign>(`/campaigns/${id}/archive`, { method: "POST" });

export const deleteCampaign = (id: number) =>
  call<null>(`/campaigns/${id}`, { method: "DELETE" });

export const duplicateCampaign = (id: number) =>
  call<ICampaign>(`/campaigns/${id}/duplicate`, { method: "POST" });

/* ── Preview ────────────────────────────────────────────────────── */
export interface IPreviewPagination {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
}
export interface IPaginatedPreview extends IPreviewResult {
  pagination?: IPreviewPagination;
}

export const previewRecipients = (
  id: number,
  filter: AudienceFilter,
  opts: { page?: number; per_page?: number; search?: string } = {},
) => {
  const qs = new URLSearchParams();
  if (opts.page)     qs.set("page",     String(opts.page));
  if (opts.per_page) qs.set("per_page", String(opts.per_page));
  if (opts.search)   qs.set("search",   opts.search);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return call<IPaginatedPreview>(`/campaigns/${id}/preview-recipients${suffix}`, {
    method: "POST", body: JSON.stringify({ filter }),
  });
};

export const previewExternal = (id: number, contacts: Array<{ name?: string; email?: string; phone?: string }>) =>
  call<IPreviewResult>(`/campaigns/${id}/preview-external`, {
    method: "POST", body: JSON.stringify({ contacts }),
  });

/* ── Send / Schedule / Test ─────────────────────────────────────── */
export const sendCampaign = (id: number, filter?: AudienceFilter) =>
  call<{ campaign: ICampaign; dispatched_count: number }>(`/campaigns/${id}/send`, {
    method: "POST", body: JSON.stringify(filter ? { filter } : {}),
  });

export const scheduleCampaign = (id: number, scheduled_at: string) =>
  call<ICampaign>(`/campaigns/${id}/schedule`, {
    method: "POST", body: JSON.stringify({ scheduled_at }),
  });

export const cancelSchedule = (id: number) =>
  call<ICampaign>(`/campaigns/${id}/cancel-schedule`, { method: "POST" });

export const testEmail = (id: number, email: string) =>
  call<{ sent_to: string }>(`/campaigns/${id}/test-email`, {
    method: "POST", body: JSON.stringify({ email }),
  });

export const testSms = (id: number, phone: string) =>
  call<{ sent_to: string; provider_status: string | null }>(`/campaigns/${id}/test-sms`, {
    method: "POST", body: JSON.stringify({ phone }),
  });
